import React, { useEffect, useMemo, useContext, useRef, useCallback } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import FilledButton from '../../../components/Buttons/FilledButton';
import BillingService from '../../../services/BillingService';
import TierService from '../../../services/TierService';
import UserService from '../../../services/UserService';
import { ORG_ROLES } from '../../../constants/main';
import { UiContext } from '../../../context/UiContext';
import PlansSection from './PlansSection';
import classes from './styles.module.scss';

export default function BillingPage() {
  const { data: me } = useQuery('me', UserService.getMe);
  const orgId = me?.organizationId;
  const isOrgOwner = me?.orgRole?.access === ORG_ROLES.OWNER;
  const trialEndsAt = me?.organizationBilling?.trialEndsAt || null;
  const isTrialExpired = me?.organizationBilling?.isTrialExpired === true;
  const hasPaidAccess = me?.organizationBilling?.hasPaidAccess === true;
  const location = useLocation();
  const navigate = useNavigate();
  const { notifySuccess, notifyError } = useContext(UiContext);
  const lastHandledSearch = useRef(null);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const rawSessionId = queryParams.get('session_id');
  const sessionId = rawSessionId && rawSessionId.includes('{') ? null : rawSessionId;
  const statusParam = queryParams.get('status');

  const { data: usage } = useQuery(
    ['org-usage', orgId],
    () => BillingService.getUsage(orgId),
    { enabled: !!orgId && isOrgOwner }
  );

  const tierSnapshot =
    usage?.data?.tier || me?.Organization?.metadata?.tierSnapshot || me?.organization?.metadata?.tierSnapshot || {};
  const subscriptionStatus = tierSnapshot.status || 'active';
  const cancelAt = tierSnapshot.cancelAt || null;

  const { data: tiers } = useQuery('tiers-public', TierService.listPublic, {
    enabled: isOrgOwner,
  });

  const { data: checkoutStatus, isLoading: statusLoading } = useQuery(
    ['billing-session', sessionId],
    () => BillingService.getCheckoutSessionStatus(sessionId),
    { enabled: !!sessionId && isOrgOwner }
  );
  const clearQueryParams = useCallback(() => {
    if (location.search) {
      navigate({ pathname: location.pathname }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isOrgOwner) return;
    if (statusLoading) return;
    if (location.search === lastHandledSearch.current) return;

    if (statusParam === 'cancel') {
      notifyError('Checkout was cancelled. No changes were made.');
      clearQueryParams();
      lastHandledSearch.current = location.search || '';
      return;
    }

    if (checkoutStatus?.data) {
      const { paymentStatus, status } = checkoutStatus.data;
      if (paymentStatus === 'paid' || status === 'complete') {
        notifySuccess('Payment succeeded. Your subscription will update shortly.');
      } else {
        notifyError('Payment incomplete. If you used another tab, please retry.');
      }
      clearQueryParams();
      lastHandledSearch.current = location.search || '';
      return;
    }

    if (statusParam === 'success' && !sessionId) {
      notifySuccess('Returned from checkout.');
      clearQueryParams();
      lastHandledSearch.current = location.search || '';
    }
  }, [
    checkoutStatus,
    clearQueryParams,
    isOrgOwner,
    notifyError,
    notifySuccess,
    sessionId,
    statusLoading,
    statusParam,
    location.pathname,
    location.search,
  ]);

  const handleUpgrade = async (tierId) => {
    try {
      const { data } = await BillingService.createCheckoutSession(tierId);
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error(error?.response?.data?.message || 'Failed to start upgrade');
    }
  };

  const handlePortal = async () => {
    try {
      const { data } = await BillingService.createPortalSession();
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error(error?.response?.data?.message || 'Failed to open billing portal');
    }
  };

  const seatsUsed = usage?.data?.seats?.used || 0;
  const seatsLimit = usage?.data?.seats?.limit || null;
  const storageUsed = usage?.data?.storage?.usedBytes || 0;
  const storageLimit = usage?.data?.storage?.limitBytes || null;
  const hasStorageLimit = Number.isFinite(storageLimit) && storageLimit > 0;
  const storageUsagePercent = hasStorageLimit
    ? Math.min(100, Math.max(0, (storageUsed / storageLimit) * 100))
    : 0;

  const trialInfo = useMemo(() => {
    if (!trialEndsAt) return null;
    if (hasPaidAccess) return null;
    const end = new Date(trialEndsAt);
    if (Number.isNaN(end.getTime())) return null;
    const diff = end.getTime() - Date.now();
    const daysRemaining = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    return { daysRemaining, end };
  }, [trialEndsAt, hasPaidAccess]);

  if (!isOrgOwner) {
    return (
      <div className={classes.BillingPage}>
        <div className={classes.errorMessage}>
          You don&apos;t have permission to manage billing. Only organization owners can access this
          page.
        </div>
      </div>
    );
  }

  return (
    <div className={classes.BillingPage}>
      <div className={classes.header}>
        <h1>Billing</h1>
        <FilledButton onClick={handlePortal}>Manage Billing</FilledButton>
      </div>

      {(subscriptionStatus && subscriptionStatus !== 'active') || cancelAt ? (
        <div className={classes.statusNotice}>
          Your subscription is pending cancellation
          {cancelAt && (
            <>
              {' '}
              scheduled to end on{' '}
              {new Date(cancelAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
              .
            </>
          )}{' '}
          If you canceled in Stripe, your subscription will end at period end.
        </div>
      ) : null}

      {trialInfo && !isTrialExpired ? (
        <div className={classes.statusNotice}>
          Trial:{' '}
          {trialInfo.daysRemaining === 0
            ? 'ends today'
            : `${trialInfo.daysRemaining} day${trialInfo.daysRemaining === 1 ? '' : 's'} left`}{' '}
          (ends on{' '}
          {trialInfo.end.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
          ).
        </div>
      ) : null}

      <div className={classes.usageGrid}>
        <div className={`${classes.card} ${classes.usageCard}`}>
          <div className={classes.label}>Organization</div>
          <div className={classes.value}>{me?.Organization?.name || me?.organization?.name || '—'}</div>
        </div>
        <div className={`${classes.card} ${classes.usageCard}`}>
          <div className={classes.label}>Users</div>
          <div className={classes.value}>
            {seatsUsed}
            {seatsLimit ? ` / ${seatsLimit}` : ''}
          </div>
        </div>
        <div className={`${classes.card} ${classes.usageCard}`}>
          <div className={classes.label}>Used Storage</div>
          {hasStorageLimit ? (
            <div className={classes.progressContainer} aria-label="Storage usage progress">
              <div className={classes.progressBackground}>
                <div
                  className={classes.progressBar}
                  style={{ width: `${storageUsagePercent}%` }}
                />
              </div>
              <span className={classes.percentage}>{Math.round(storageUsagePercent)}%</span>
            </div>
          ) : null}
        </div>
      </div>

      <PlansSection
        tiers={tiers?.data || []}
        tierSnapshot={tierSnapshot}
        onUpgrade={handleUpgrade}
        onPortal={handlePortal}
      />
    </div>
  );
}
